import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Text,
} from "@chakra-ui/react";
import { useStytch } from "@stytch/react";
import {
  AuthFactorInstanceProps,
  RecoveryAuthFactorInstance,
} from "features/auth/AuthFactor";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import {
  getStytchErrorMessage,
  getStytchSessionDurationMinutes,
  isStytchSdkApiError,
} from "features/auth/StytchUtils";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import Form from "shared/components/form/Form";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { useToast } from "shared/components/toast/useToast";

interface RecoveryFormData {
  recovery_code: string;
}

export const AuthFactorRecovery: React.FC<
  AuthFactorInstanceProps<RecoveryAuthFactorInstance>
> = ({ onSuccess, onCancel, rememberDevice }) => {
  const { t } = useTranslation(["common", "auth"]);
  const { showErrorToast } = useToast();

  const stytchClient = useStytch();

  const onSubmit = async ({ recovery_code }: RecoveryFormData) => {
    await stytchClient.totps.recover({
      recovery_code,
      session_duration_minutes: getStytchSessionDurationMinutes(rememberDevice),
    });
  };

  const form = useForm<RecoveryFormData>();

  return (
    <Form
      form={form}
      disableNavBlocker={true}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      onError={(error) => {
        if (isStytchSdkApiError(error)) {
          if (error.error_type === "invalid_recovery_code") {
            form.setError("recovery_code", {
              message: t("auth:errors.incorrectRecoveryCode"),
            });
          }
          showErrorToast(getStytchErrorMessage(t, error));
          return true;
        }
      }}
    >
      <Card width="md">
        <CardHeader>
          <Box>
            <Text textStyle="h4">{t("auth:recover")}</Text>
            <Text pt="24px">{t("auth:recoveryCodeInstructions")}</Text>
          </Box>
          <CardCloseButton onClick={onCancel} />
        </CardHeader>
        <CardBody pb={0}>
          <FormTextInput
            name="recovery_code"
            title={t("auth:code")}
            autoFocus={true}
          />
        </CardBody>
        <CardFooter>
          <AuthHelpButton />
          <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
        </CardFooter>
      </Card>
    </Form>
  );
};
